import { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { upi, INSTRUMENT_LABELS, type Attestation } from '../../api/upi';
import MandateTermsFields, { EMPTY_TERMS, constraintsFromTerms, type TermsForm } from './MandateTermsFields';

interface Props {
  open: boolean;
  agentDid: string;
  onClose: () => void;
  onProposed: () => void;
}

const input = 'mt-1 w-full rounded-lg border border-border-subtle px-3 py-2 text-xs';

/**
 * The agent's operator proposes a mandate. The customer sees it in their
 * wallet with its purpose and decides; nothing is granted until they sign.
 */
export default function ProposeMandateDialog({ open, agentDid, onClose, onProposed }: Props) {
  const [atts, setAtts] = useState<Attestation[]>([]);
  const [attId, setAttId] = useState('');
  const [customer, setCustomer] = useState('');
  const [purpose, setPurpose] = useState('');
  const [terms, setTerms] = useState<TermsForm>(EMPTY_TERMS);
  const [validUntil, setValidUntil] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    upi.attestations(agentDid).then((list) => setAtts(list.filter((a) => a.usable))).catch(() => setAtts([]));
  }, [open, agentDid]);

  const att = atts.find((a) => a.id === attId);
  const today = new Date().toISOString().slice(0, 10);

  const submit = async () => {
    setError('');
    if (!att) return setError('Choose the bank instrument the mandate draws on.');
    if (!customer.trim()) return setError("Enter the customer's DID.");
    if (!purpose.trim()) return setError('State what the mandate is for; the customer consents to this purpose.');
    if (!validUntil) return setError('Choose when the mandate ends.');
    const { constraints, error: termsError } = constraintsFromTerms(terms);
    if (termsError) return setError(termsError);
    setBusy(true);
    try {
      await upi.propose({
        customerDid: customer.trim(), agentDid, purpose: purpose.trim(), constraints,
        validUntil: new Date(`${validUntil}T23:59:59Z`).toISOString().replace(/\.\d{3}Z$/, 'Z'),
        instrument: { kind: att.kind, ref: att.instrumentRef, attestation: att.id },
      });
      onProposed();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { if (!busy) onClose(); }} title="Propose a payment mandate" wide>
      <div className="space-y-4 text-sm">
        <p className="text-text-muted">
          The customer reviews this in their wallet and signs it if they agree. It is checked now against the bank's limits,
          so they are never asked to sign something the bank would refuse.
        </p>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Bank instrument</span>
          <select id="propose-instrument" value={attId} onChange={(e) => setAttId(e.target.value)} className={input}>
            <option value="">{atts.length ? 'Choose…' : 'No usable instrument: the customer must set one up at their bank'}</option>
            {atts.map((a) => (
              <option key={a.id} value={a.id}>{INSTRUMENT_LABELS[a.kind] ?? a.kind} · {a.instrumentRef}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Customer DID</span>
          <input id="propose-customer" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="did:key:z6Mk… or did:adi:…" className={`${input} font-mono`} />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-text-muted">Purpose</span>
          <input id="propose-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="Weekly groceries from FreshMart" className={input} />
        </label>
        <MandateTermsFields value={terms} onChange={setTerms} bankLimits={att?.limits} />
        <label className="block sm:w-1/3">
          <span className="text-xs font-semibold text-text-muted">Valid until</span>
          <input id="propose-valid-until" type="date" min={today} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className={input} />
        </label>
        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="rounded-lg border border-border-subtle px-4 py-2 font-medium text-text-main hover:bg-slate-50 disabled:opacity-50">Cancel</button>
          <button onClick={() => void submit()} disabled={busy} className="btn-primary disabled:opacity-50">{busy ? 'Sending…' : 'Send to customer'}</button>
        </div>
      </div>
    </Modal>
  );
}
